import React, { useState, useMemo } from 'react';
import { Search, Filter } from 'lucide-react';
import EPaperCard from './EPaperCard';
import { EPaperListProps } from '../types/epaper';
import ViewToggle, { ViewMode } from './ViewToggle';
import Pagination from './Pagination';
import './EPaperList.css';

const EPaperList: React.FC<EPaperListProps> = ({ epapers, onSelectEPaper, loading = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState<string>('all');
  const [currentView, setCurrentView] = useState<ViewMode>('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  // Get unique years for filter
  const years = useMemo(() => {
    const yearSet = new Set(epapers.map(epaper => new Date(epaper.date).getFullYear().toString()));
    return Array.from(yearSet).sort((a, b) => parseInt(b) - parseInt(a));
  }, [epapers]);

  // Filter and sort epapers based on search and year
  const filteredEpapers = useMemo(() => {
    const filtered = epapers.filter(epaper => {
      const matchesSearch = epaper.title.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesYear = selectedYear === 'all' || 
        new Date(epaper.date).getFullYear().toString() === selectedYear;
      return matchesSearch && matchesYear;
    });
    
    // Sort by date (newest first)
    return filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [epapers, searchTerm, selectedYear]);

  // Pagination logic
  const totalPages = Math.ceil(filteredEpapers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentEpapers = filteredEpapers.slice(startIndex, endIndex);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedYear]);

  if (loading) {
    return (
      <div className="epaper-list-loading">
        <div className="spinner"></div>
        <p>Loading E-Papers...</p>
      </div>
    );
  }

  return (
    <div className="epaper-list-container">
      <div className="epaper-list-header">
        <h1>Weekly E-Papers</h1>
        <p>Browse and read our weekly E-Papers</p>
      </div>

      <div className="epaper-list-controls">
        <div className="controls-left">
          <div className="search-container">
            <Search size={20} />
            <input
              type="text"
              placeholder="Search E-Papers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filter-container">
            <Filter size={20} />
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="year-filter"
            >
              <option value="all">All Years</option>
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="controls-right">
          <ViewToggle 
            currentView={currentView}
            onViewChange={setCurrentView}
          />
        </div>
      </div>

      <div className="epaper-list-stats">
        <span>{filteredEpapers.length} of {epapers.length} E-Papers</span>
      </div>

      {currentEpapers.length === 0 ? (
        <div className="epaper-list-empty">
          <p>No E-Papers found matching your criteria.</p>
        </div>
      ) : (
        <>
          <div className={`epaper-container ${currentView === 'list' ? 'epaper-list-view' : 'epaper-grid'}`}>
            {currentEpapers.map((epaper) => (
              <EPaperCard
                key={epaper.id}
                epaper={epaper}
                onClick={() => onSelectEPaper(epaper)}
                viewMode={currentView}
              />
            ))}
          </div>
          
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              totalItems={filteredEpapers.length}
            />
          )}
        </>
      )}
    </div>
  );
};

export default EPaperList; 